import React, { useEffect, useState } from 'react';
import { Save, Plus, Trash2, Server, MessageCircle, Tag } from 'lucide-react';
import { api } from '../lib/api';
import { UserCategory, UserSettings } from '../types';
import { ConfirmModal, Spinner, useToast, Toast } from '../components/UI';

const CAT_COLORS = ['#6c63ff','#25d366','#f4c542','#ff6b6b','#e879f9','#38bdf8','#fb923c','#a3e635','#f97316','#06b6d4'];

const EMPTY: UserSettings = {
  smtpHost: '', smtpPort: '587', smtpUser: '', smtpPass: '', smtpFrom: '', smtpFromName: '',
  waPhoneNumberId: '', waAccessToken: '', waBusinessAccountId: '', waVerifyToken: '',
};

type Tab = 'email' | 'whatsapp' | 'categories';

export default function SettingsPage(): React.ReactElement {
  const { toast, show } = useToast();
  const [tab, setTab] = useState<Tab>('email');
  const [settings, setSettings] = useState<UserSettings>(EMPTY);
  const [categories, setCategories] = useState<UserCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState(CAT_COLORS[0]);
  const [addingCat, setAddingCat] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<UserCategory | null>(null);

  useEffect(() => {
    Promise.all([api.getSettings(), api.getCategories()])
      .then(([s, c]) => { if (s) setSettings({ ...EMPTY, ...s }); setCategories(c); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const upd = (k: keyof UserSettings) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setSettings(s => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try { await api.saveSettings(settings); show('Settings saved', 'success'); }
    catch (e: any) { show(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const addCategory = async () => {
    if (!newCatName.trim()) { show('Name required', 'error'); return; }
    if (categories.find(c => c.name.toLowerCase() === newCatName.trim().toLowerCase())) {
      show('Category already exists', 'error'); return;
    }
    setAddingCat(true);
    try {
      const created = await api.createCategory({ name: newCatName.trim(), color: newCatColor });
      setCategories(p => [...p, created]);
      setNewCatName(''); setNewCatColor(CAT_COLORS[0]);
      show('Category added', 'success');
    } catch (e: any) { show(e.message, 'error'); }
    finally { setAddingCat(false); }
  };

  const deleteCategory = async () => {
    if (!deleteTarget) return;
    await api.deleteCategory(deleteTarget.id).catch(() => null);
    setCategories(p => p.filter(c => c.id !== deleteTarget.id));
    show('Category deleted', 'info'); setDeleteTarget(null);
  };

  if (loading) return <div className="page" style={{ display:'flex', justifyContent:'center', paddingTop:80 }}><Spinner size={28} /></div>;

  return (
    <div className="page fade-in">
      {toast && <Toast msg={toast.msg} type={toast.type} />}

      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-sub">Configure integrations and manage categories</p>
        </div>
      </div>

      {/* Tab strip */}
      <div className="tab-strip">
        {([
          { id: 'email', icon: Server, label: 'Email (SMTP)' },
          { id: 'whatsapp', icon: MessageCircle, label: 'WhatsApp API' },
          { id: 'categories', icon: Tag, label: 'Categories' },
        ] as { id: Tab; icon: any; label: string }[]).map(({ id, icon: Icon, label }) => (
          <button key={id} className={`tab ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>
            <Icon size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />{label}
          </button>
        ))}
      </div>

      {/* Email SMTP */}
      {tab === 'email' && (
        <div className="settings-section">
          <div className="settings-section-title">SMTP Configuration</div>
          <div className="settings-section-desc">Use your own email server to send outreach emails. Works with Gmail, Zoho, SendGrid, Mailgun and any SMTP provider.</div>
          <div className="form-row">
            <div className="form-group"><label>SMTP Host</label><input placeholder="smtp.gmail.com" value={settings.smtpHost} onChange={upd('smtpHost')} /></div>
            <div className="form-group"><label>Port</label><input placeholder="587" value={settings.smtpPort} onChange={upd('smtpPort')} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>SMTP Username</label><input placeholder="you@gmail.com" value={settings.smtpUser} onChange={upd('smtpUser')} /></div>
            <div className="form-group"><label>SMTP Password / App Password</label><input type="password" placeholder="••••••••••••" value={settings.smtpPass} onChange={upd('smtpPass')} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>From Email</label><input placeholder="hello@yourcompany.com" value={settings.smtpFrom} onChange={upd('smtpFrom')} /></div>
            <div className="form-group"><label>From Name</label><input placeholder="Webiconic" value={settings.smtpFromName} onChange={upd('smtpFromName')} /></div>
          </div>
          <div style={{ background: 'rgba(108,99,255,0.06)', border: '1px solid rgba(108,99,255,0.15)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--text2)', marginBottom: 20 }}>
            💡 For Gmail: use an <strong style={{ color: 'var(--text)' }}>App Password</strong> (not your regular password). Enable 2FA → Google Account → Security → App Passwords.
          </div>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <Spinner size={14} /> : <Save size={14} />} Save Email Settings
          </button>
        </div>
      )}

      {/* WhatsApp */}
      {tab === 'whatsapp' && (
        <div className="settings-section">
          <div className="settings-section-title">Meta Cloud API (WhatsApp)</div>
          <div className="settings-section-desc">Connect your Meta WhatsApp Business account. You need a verified Meta Business account and an approved app.</div>
          <div className="form-group"><label>Phone Number ID</label><input placeholder="From Meta Developer Console" value={settings.waPhoneNumberId} onChange={upd('waPhoneNumberId')} /></div>
          <div className="form-group"><label>Permanent Access Token</label><input type="password" placeholder="EAAxxxxxxxx..." value={settings.waAccessToken} onChange={upd('waAccessToken')} /></div>
          <div className="form-group"><label>WhatsApp Business Account ID</label><input placeholder="From Meta Business Manager" value={settings.waBusinessAccountId} onChange={upd('waBusinessAccountId')} /></div>
          <div className="form-group"><label>Webhook Verify Token (optional)</label><input placeholder="Any secret string you choose" value={settings.waVerifyToken} onChange={upd('waVerifyToken')} /></div>
          <div style={{ background: 'rgba(37,211,102,0.06)', border: '1px solid rgba(37,211,102,0.15)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--text2)', marginBottom: 20 }}>
            💡 Go to <strong style={{ color: 'var(--text)' }}>developers.facebook.com</strong> → Your App → WhatsApp → API Setup to find these credentials.
          </div>
          <button className="btn btn-primary" style={{ background: 'var(--accent3)', boxShadow: '0 4px 20px rgba(37,211,102,0.25)' }} onClick={save} disabled={saving}>
            {saving ? <Spinner size={14} /> : <Save size={14} />} Save WhatsApp Settings
          </button>
        </div>
      )}

      {/* Categories */}
      {tab === 'categories' && (
        <div className="settings-section">
          <div className="settings-section-title">Lead Categories</div>
          <div className="settings-section-desc">Organise your leads with custom categories. Each category gets a colour for easy identification.</div>

          {/* Existing categories */}
          {categories.length === 0 ? (
            <div className="empty-state" style={{ padding: '32px 0' }}>
              <Tag size={28} />
              <p>No categories yet — add your first one below</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
              {categories.map(cat => (
                <div key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--surface2)', borderRadius: 9, border: '1px solid var(--border)' }}>
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: cat.color, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{cat.name}</span>
                  <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(cat)}>
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add new */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text3)', marginBottom: 14 }}>Add Category</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label>Name</label>
                <input placeholder="e.g. Startup, Retail, Agency…" value={newCatName}
                  onChange={e => setNewCatName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addCategory()} />
              </div>
              <div style={{ marginBottom: 0 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Colour</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {CAT_COLORS.map(c => (
                    <div key={c} className={`color-dot ${newCatColor === c ? 'picked' : ''}`}
                      style={{ background: c }} onClick={() => setNewCatColor(c)} />
                  ))}
                </div>
              </div>
              <button className="btn btn-primary" onClick={addCategory} disabled={addingCat} style={{ flexShrink: 0, marginBottom: 0 }}>
                {addingCat ? <Spinner size={14} /> : <Plus size={14} />} Add
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Category"
          message={<>Delete <strong>{deleteTarget.name}</strong>? Leads with this category will keep the label but it won't appear in filters.</>}
          onConfirm={deleteCategory} onCancel={() => setDeleteTarget(null)} danger
        />
      )}
    </div>
  );
}
